/**
 * Date utility functions for UTC+3 (Istanbul) timezone
 * All dates are stored in UTC but displayed as Istanbul time (UTC+3)
 */

const ISTANBUL_OFFSET_MS = 3 * 60 * 60 * 1000;

/**
 * Backend DateTime'ı (Z'li veya Z'siz ISO) gerçek UTC anına çevirir.
 * EF/SQL Unspecified değerler Z olmadan gelir; bunlar UTC kabul edilir.
 * @returns {Date|null}
 */
export const parseBackendDate = (dateValue) => {
  if (!dateValue && dateValue !== 0) return null;
  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    if (typeof dateValue !== 'string') {
      const d = new Date(dateValue);
      return isNaN(d.getTime()) ? null : d;
    }

    const s = dateValue.trim();
    if (!s) return null;

    // YYYY-MM-DD → İstanbul gününün 00:00'ı (UTC = TR gece yarısı - 3 saat)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [year, month, day] = s.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - ISTANBUL_OFFSET_MS);
    }

    // ISO: YYYY-MM-DDTHH:mm[:ss][.fff]  (Z/offset yok → UTC)
    const isoLocal = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/.exec(s);
    if (isoLocal) {
      const year = parseInt(isoLocal[1], 10);
      const month = parseInt(isoLocal[2], 10) - 1;
      const day = parseInt(isoLocal[3], 10);
      const hours = parseInt(isoLocal[4], 10);
      const minutes = parseInt(isoLocal[5], 10);
      const seconds = parseInt(isoLocal[6] || '0', 10);
      return new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    }

    // Z veya offset varsa native parse
    if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s) || /[+-]\d{4}$/.test(s)) {
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  } catch (e) {
    console.warn('parseBackendDate error:', e, dateValue);
    return null;
  }
};

const toIstanbulParts = (dateValue) => {
  const dateObj = parseBackendDate(dateValue);
  if (!dateObj) return null;
  const istanbulDate = new Date(dateObj.getTime() + ISTANBUL_OFFSET_MS);
  return {
    year: istanbulDate.getUTCFullYear(),
    month: istanbulDate.getUTCMonth() + 1,
    day: istanbulDate.getUTCDate(),
    hours: istanbulDate.getUTCHours(),
    minutes: istanbulDate.getUTCMinutes(),
    seconds: istanbulDate.getUTCSeconds()
  };
};

/**
 * Convert a date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm) to ISO string (UTC)
 * The input date is interpreted as Istanbul time (UTC+3)
 */
export const toISODateOrNull = (dateValue) => {
  if (!dateValue || dateValue === '' || dateValue === null || dateValue === undefined) {
    return null;
  }
  try {
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - ISTANBUL_OFFSET_MS).toISOString();
    }
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(dateValue)) {
      const [datePart, timePart] = dateValue.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = (timePart || '00:00').split(':').map(Number);
      const istanbulMs = Date.UTC(year, month - 1, day, hours || 0, minutes || 0, 0, 0);
      return new Date(istanbulMs - ISTANBUL_OFFSET_MS).toISOString();
    }
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString();
  } catch (e) {
    console.warn('Date conversion error:', e, dateValue);
    return null;
  }
};

/**
 * Convert an ISO date string (UTC) to Istanbul timezone date string (YYYY-MM-DDTHH:mm)
 */
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  try {
    const p = toIstanbulParts(dateValue);
    if (!p) return '';
    const month = String(p.month).padStart(2, '0');
    const day = String(p.day).padStart(2, '0');
    const hours = String(p.hours).padStart(2, '0');
    const minutes = String(p.minutes).padStart(2, '0');
    return `${p.year}-${month}-${day}T${hours}:${minutes}`;
  } catch (e) {
    return '';
  }
};

/**
 * Convert an ISO date string (UTC) to Istanbul timezone date string (YYYY-MM-DD) for date input
 */
export const formatDateForDateInput = (dateValue) => {
  if (!dateValue) return '';
  try {
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }
    const p = toIstanbulParts(dateValue);
    if (!p) return '';
    const month = String(p.month).padStart(2, '0');
    const day = String(p.day).padStart(2, '0');
    return `${p.year}-${month}-${day}`;
  } catch (e) {
    console.error('formatDateForDateInput error:', e, dateValue);
    return '';
  }
};

/**
 * Format date for display (Istanbul timezone) - DATE ONLY (no time)
 * @returns {string} - DD.MM.YYYY
 */
export const formatDateOnly = (dateValue) => {
  if (!dateValue) return '-';
  try {
    const p = toIstanbulParts(dateValue);
    if (!p) return '-';
    const day = String(p.day).padStart(2, '0');
    const month = String(p.month).padStart(2, '0');
    return `${day}.${month}.${p.year}`;
  } catch (e) {
    console.error('formatDateOnly error:', e, dateValue);
    return '-';
  }
};

/**
 * Format date for display (Istanbul timezone) with time
 * @returns {string} - DD.MM.YYYY HH:mm
 */
export const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return '-';
  try {
    const p = toIstanbulParts(dateValue);
    if (!p) return '-';
    const day = String(p.day).padStart(2, '0');
    const month = String(p.month).padStart(2, '0');
    const hours = String(p.hours).padStart(2, '0');
    const minutes = String(p.minutes).padStart(2, '0');
    return `${day}.${month}.${p.year} ${hours}:${minutes}`;
  } catch (e) {
    console.error('formatDateForDisplay error:', e, dateValue);
    return '-';
  }
};

/**
 * İş son tarihi: gün sonu 23:59 olmalı.
 * Eski kayıtlarda UTC/İstanbul karışınca 20:59 görünenler 23:59'a çekilir.
 */
/**
 * Durusma tarihi: saat girilmisse tarih + saat, girilmemisse yalnizca tarih.
 * Saat 00:00 ise gostermek anlamsiz oldugu icin gizlenir.
 */
export const formatDurusmaTarihi = (dateValue) => {
  if (!dateValue) return null;
  try {
    const p = toIstanbulParts(dateValue);
    if (!p) return null;
    const gun = String(p.day).padStart(2, '0');
    const ay = String(p.month).padStart(2, '0');
    const tarih = gun + '.' + ay + '.' + p.year;
    if (p.hours === 0 && p.minutes === 0) return tarih;
    const saat = String(p.hours).padStart(2, '0');
    const dakika = String(p.minutes).padStart(2, '0');
    return tarih + ' ' + saat + ':' + dakika;
  } catch (e) {
    return null;
  }
};

export const formatJobDueDateForDisplay = (dateValue) => {
  if (!dateValue) return '-';
  try {
    const p = toIstanbulParts(dateValue);
    if (!p) return '-';
    let { hours, minutes } = p;
    if ((hours === 20 && minutes === 59) || (hours === 21 && minutes === 0)) {
      hours = 23;
      minutes = 59;
    }
    const day = String(p.day).padStart(2, '0');
    const month = String(p.month).padStart(2, '0');
    return `${day}.${month}.${p.year} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  } catch (e) {
    return formatDateForDisplay(dateValue);
  }
};

/**
 * Format date to YYYY-MM-DD string (Istanbul timezone) for calendar comparisons
 */
export const formatDateToLocalString = (dateValue) => {
  if (!dateValue) return '';
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }
  try {
    const p = toIstanbulParts(dateValue);
    if (!p) return '';
    const month = String(p.month).padStart(2, '0');
    const day = String(p.day).padStart(2, '0');
    return `${p.year}-${month}-${day}`;
  } catch (e) {
    return '';
  }
};

/**
 * Get today's date in UTC+3 (Istanbul) timezone as ISO string
 */
export const getTodayISOString = () => {
  return toISODateOrNull(getTodayDateString());
};

/**
 * Get today's date in UTC+3 (Istanbul) timezone as YYYY-MM-DD string
 * Browser yerel saat diliminden bağımsız (UTC epoch + 3 saat).
 */
export const getTodayDateString = () => {
  const istanbulDate = new Date(Date.now() + ISTANBUL_OFFSET_MS);
  const year = istanbulDate.getUTCFullYear();
  const month = String(istanbulDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(istanbulDate.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Son tarih geçmiş mi? (UTC an karşılaştırması)
 */
export const isOverdueDate = (dateValue, now = new Date()) => {
  const d = parseBackendDate(dateValue);
  if (!d) return false;
  return d.getTime() < now.getTime();
};
